/**
 * Interface localization (i18n) - types.
 *
 * SCOPE: this localizes the Nuvra INTERFACE (chrome: navigation, menus, settings).
 * It is deliberately separate from app-OUTPUT language (the language of the software
 * Nuvra generates), which is a different capability and is AI-key-gated.
 */

export type Locale =
  | "en" | "es" | "fr" | "pt" | "de" | "it" | "nl" | "pl" | "ru" | "uk"
  | "ar" | "he" | "fa" | "tr" | "hi" | "bn" | "id" | "ms" | "th" | "vi"
  | "zh" | "ja" | "ko" | "sw" | "yo" | "ha" | "ig" | "am" | "zu" | "af"
  | "so" | "rw" | "sn" | "om" | "wo";

export interface LocaleOption {
  code:        Locale;
  /** Name in the language's own script, shown in the switcher. */
  nativeLabel: string;
  /** ISO 639-1 dir attribute for RTL languages. */
  dir?: "ltr" | "rtl";
  /** World region grouping for display in the switcher. */
  region: "global" | "europe" | "africa" | "asia" | "middle_east" | "americas";
}

export const SUPPORTED_LOCALES: LocaleOption[] = [
  // Global / Americas
  { code: "en", nativeLabel: "English",             region: "global"     },
  { code: "es", nativeLabel: "Español",             region: "americas"   },
  { code: "pt", nativeLabel: "Português",           region: "americas"   },
  // Europe
  { code: "fr", nativeLabel: "Français",            region: "europe"     },
  { code: "de", nativeLabel: "Deutsch",             region: "europe"     },
  { code: "it", nativeLabel: "Italiano",            region: "europe"     },
  { code: "nl", nativeLabel: "Nederlands",          region: "europe"     },
  { code: "pl", nativeLabel: "Polski",              region: "europe"     },
  { code: "ru", nativeLabel: "Русский",             region: "europe"     },
  { code: "uk", nativeLabel: "Українська",          region: "europe"     },
  // Middle East
  { code: "ar", nativeLabel: "العربية",             region: "middle_east", dir: "rtl" },
  { code: "he", nativeLabel: "עברית",               region: "middle_east", dir: "rtl" },
  { code: "fa", nativeLabel: "فارسی",               region: "middle_east", dir: "rtl" },
  { code: "tr", nativeLabel: "Türkçe",              region: "middle_east" },
  // Asia
  { code: "hi", nativeLabel: "हिन्दी",               region: "asia"       },
  { code: "bn", nativeLabel: "বাংলা",               region: "asia"       },
  { code: "id", nativeLabel: "Bahasa Indonesia",    region: "asia"       },
  { code: "ms", nativeLabel: "Bahasa Melayu",       region: "asia"       },
  { code: "th", nativeLabel: "ภาษาไทย",             region: "asia"       },
  { code: "vi", nativeLabel: "Tiếng Việt",          region: "asia"       },
  { code: "zh", nativeLabel: "中文 (简体)",           region: "asia"       },
  { code: "ja", nativeLabel: "日本語",               region: "asia"       },
  { code: "ko", nativeLabel: "한국어",               region: "asia"       },
  // Africa: major languages
  { code: "sw", nativeLabel: "Kiswahili",           region: "africa"     },
  { code: "yo", nativeLabel: "Yorùbá",              region: "africa"     },
  { code: "ha", nativeLabel: "Hausa",               region: "africa"     },
  { code: "ig", nativeLabel: "Igbo",                region: "africa"     },
  { code: "am", nativeLabel: "አማርኛ",               region: "africa"     },
  { code: "zu", nativeLabel: "isiZulu",             region: "africa"     },
  { code: "af", nativeLabel: "Afrikaans",           region: "africa"     },
  { code: "so", nativeLabel: "Soomaali",            region: "africa"     },
  { code: "rw", nativeLabel: "Kinyarwanda",         region: "africa"     },
  { code: "sn", nativeLabel: "chiShona",            region: "africa"     },
  { code: "om", nativeLabel: "Afaan Oromoo",        region: "africa"     },
  { code: "wo", nativeLabel: "Wolof",               region: "africa"     },
];

export const DEFAULT_LOCALE: Locale = "en";

/** A message catalog: flat dotted keys mapped to display strings. */
export type Messages = Record<string, string>;
